import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertOctagon } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Top-level React error boundary.
 *
 * Catches synchronous render errors anywhere in the React tree and replaces
 * the white-screen-of-death with a branded fallback page. Without this, a
 * single bad `.map(undefined)` in any renderer would unmount the entire app.
 *
 * Class component because React's error-boundary contract requires
 * ``componentDidCatch`` / ``getDerivedStateFromError`` — there is no hook
 * equivalent today. Render-phase errors only; async errors (rejected
 * promises in event handlers) bypass this boundary and are surfaced via
 * the TanStack Query / MutationCache toast hooks instead.
 *
 * The fallback page reads the current location via ``window.location``
 * rather than TanStack Router hooks — the router itself may be the thing
 * that crashed, and the boundary must be resilient to that.
 */

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // In dev this prints with the React component stack which is the most
    // useful place to read it. In prod we'd forward to Sentry / equivalent
    // here once observability lands.
    // eslint-disable-next-line no-console
    console.error('Unhandled render error:', error, info);
  }

  private handleReset = () => {
    this.setState({ error: null });
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen w-full bg-bg flex flex-col items-center justify-center px-6 py-16">
        <div className="w-full max-w-[520px] text-center">
          <img
            src="/regara_logo_1.png"
            alt="REGARA"
            className="h-6 w-auto mx-auto mb-7 block opacity-90"
          />

          <div className="inline-flex items-center justify-center size-12 rounded-full bg-state-error-bg text-state-error-accent mb-4">
            <AlertOctagon className="size-6" />
          </div>

          <h1
            className="text-navy m-0 mb-3 leading-[1.15]"
            style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 'clamp(28px, 4vw, 36px)',
            }}
          >
            Something went wrong on this page.
          </h1>

          <p className="text-muted text-[15px] m-0 mb-2 leading-relaxed">
            We hit an unexpected error while rendering this view. Your work
            is still saved on the server — nothing was lost.
          </p>
          <p className="text-muted text-[13px] m-0 mb-8">
            Try reloading the page. If the problem keeps happening, head
            back to the dashboard.
          </p>

          <div className="flex items-center justify-center gap-3">
            <Button type="button" variant="outline" onClick={this.handleReload}>
              Reload page
            </Button>
            <Button
              type="button"
              variant="brand"
              onClick={() => {
                this.handleReset();
                window.location.assign('/dashboard');
              }}
            >
              Return to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
